"use client";
import {
  createElement,
  forwardRef,
  type HTMLAttributes,
  type AnchorHTMLAttributes,
  type LabelHTMLAttributes,
} from "react";
import { Slot } from "@radix-ui/react-slot";
export type Tone =
  | "default"
  | "muted"
  | "brand"
  | "inverse"
  | "error"
  | "success"
  | "warning"
  | "info";
export type HeadingProps = HTMLAttributes<HTMLHeadingElement> & {
  as?: "h1" | "h2" | "h3" | "h4" | "h5" | "h6" | "div" | "span";
  variant?: "display" | "section" | "card" | "title";
  tone?: Tone;
  asChild?: boolean;
};
export const Heading = forwardRef<HTMLHeadingElement, HeadingProps>(
  function Heading(
    {
      as = "h2",
      variant = "section",
      tone = "brand",
      asChild,
      className = "",
      ...props
    },
    ref,
  ) {
    return createElement(asChild ? Slot : as, {
      ...props,
      ref,
      className: `dx-type-heading dx-type-heading--${variant} dx-tone--${tone} ${className}`,
    });
  },
);
export type TextProps = AnchorHTMLAttributes<HTMLElement> & {
  as?: "p" | "span" | "div" | "a";
  size?: "caption" | "small" | "body" | "lead";
  tone?: Tone;
  asChild?: boolean;
};
export const Text = forwardRef<HTMLElement, TextProps>(function Text(
  {
    as = "p",
    size = "body",
    tone = "default",
    asChild,
    className = "",
    ...props
  },
  ref,
) {
  return createElement(asChild ? Slot : as, {
    ...props,
    ref,
    className: `dx-type-text dx-type-text--${size} dx-tone--${tone} ${className}`,
  });
});
export type LabelProps = LabelHTMLAttributes<HTMLLabelElement> & {
  as?: "label" | "span" | "p" | "div";
  variant?: "field" | "eyebrow" | "caption";
  tone?: Tone;
  asChild?: boolean;
};
export const Label = forwardRef<HTMLLabelElement, LabelProps>(function Label(
  {
    as = "label",
    variant = "field",
    tone = "default",
    asChild,
    className = "",
    ...props
  },
  ref,
) {
  return createElement(asChild ? Slot : as, {
    ...props,
    ref,
    className: `dx-label dx-label--${variant} dx-tone--${tone} ${className}`,
  });
});
